import { useId, useRef, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { getOverlayRoot, useDialogBehavior } from './overlay'
import { cx } from './cx'

interface OverlayProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  footer?: ReactNode
  children?: ReactNode
  initialFocusRef?: RefObject<HTMLElement>
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      aria-label="Close"
      onClick={onClose}
      className="flex h-8 w-8 flex-none items-center justify-center rounded-md text-ink-secondary hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-focus"
    >
      <X className="h-4 w-4" aria-hidden="true" />
    </button>
  )
}

/** Centered modal dialog */
export function Dialog({ open, onClose, title, description, footer, children, initialFocusRef, size = 'md' }: OverlayProps & { size?: 'sm' | 'md' | 'lg' }) {
  const ref = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const descriptionId = useId()
  useDialogBehavior(open, onClose, ref, initialFocusRef)
  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/40" aria-hidden="true" onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cx(
          'relative flex max-h-[calc(100vh-2rem)] w-full flex-col rounded-xl border border-line bg-surface text-ink shadow-elevated focus:outline-none',
          size === 'sm' ? 'max-w-md' : size === 'lg' ? 'max-w-3xl' : 'max-w-lg'
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="flex flex-col gap-1">
            <h2 id={titleId} className="m-0 font-heading text-lg font-semibold">{title}</h2>
            {description && <p id={descriptionId} className="m-0 text-sm text-ink-secondary">{description}</p>}
          </div>
          <CloseButton onClose={onClose} />
        </div>
        {children && <div className="overflow-y-auto px-5 py-4">{children}</div>}
        {footer && <div className="flex justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
      </div>
    </div>,
    getOverlayRoot()
  )
}

/** Side panel for record details */
export function Drawer({ open, onClose, title, description, footer, children, initialFocusRef }: OverlayProps) {
  const ref = useRef<HTMLDivElement>(null)
  const titleId = useId()
  useDialogBehavior(open, onClose, ref, initialFocusRef)
  if (!open) return null

  return createPortal(
    <div className="fixed inset-0">
      <div className="absolute inset-0 bg-ink/30" aria-hidden="true" onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="absolute inset-y-0 right-0 flex w-full max-w-xl flex-col border-l border-line bg-surface text-ink shadow-elevated focus:outline-none animate-slide-in-right"
      >
        <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="flex min-w-0 flex-col gap-1">
            <h2 id={titleId} className="m-0 truncate font-heading text-lg font-semibold">{title}</h2>
            {description && <div className="text-sm text-ink-secondary">{description}</div>}
          </div>
          <CloseButton onClose={onClose} />
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
      </div>
    </div>,
    getOverlayRoot()
  )
}
